import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { MoneyApi } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import {
	getLastMonthFilterBetween,
	getReportCustomFieldsResources,
	reportParamsConverter,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { getBasicGroups, getBasicGroupsInfo } from "../../../../../../../../utils/reports/ReportUtils";
import { getCommonFilters } from "../../utils/reportCommonConfig";
import { convertFilterValuesToSnakeCase } from "../../utils/reportUtils";

const listName = "PRODUCT_REPORT";
const resourceType = "PRODUCT_REPORT";

const INITIAL_SELECTED_COLUMNS = [
	"start_amount",
	"start_fifo_price_money",
	"purchase_amount",
	"purchase_price_money",
	"sold_amount",
	"sold_fifo_price_money",
	"transfer_amount",
	"transfer_fifo_price_money",
	"stocktaking_amount",
	"stocktaking_fifo_price_money",
	"end_amount",
	"end_fifo_price_money",
];

const Report = ({ resource }: { resource?: Record<string, any> }) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();

	let config = {
		reportConfigId: "PRODUCT_REPORT",
		aggregatePrefix: "summary",
		type: "bill",
		selectedColumns: INITIAL_SELECTED_COLUMNS,
		showPercent: true,
		selectedGroups: ["PRODUCT"],
		groups: getBasicGroups(t, organization.id),
		groupsInfo: getBasicGroupsInfo(t, organization),
		columns: [
			{
				id: "start_amount",
				name: t("common.word.start_amount"),
			},
			{
				id: "start_fifo_price_money",
				name: t("common.word.start_fifo_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "purchase_amount",
				name: t("common.word.purchase_amount"),
			},
			{
				id: "purchase_price_money",
				name: t("common.word.purchase_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "sold_amount",
				name: t("modules.product_report.field.sold_amount.title"),
			},
			{
				id: "sold_fifo_price_money",
				name: t("common.word.sold_fifo_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "bundle_amount",
				name: t("modules.product.field.bundle_amount.title"),
			},
			{
				id: "bundle_fifo_price_money",
				name: t("modules.product.field.bundle_fifo_price_money.title"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "transfer_amount",
				name: t("common.word.transfer_amount"),
			},
			{
				id: "transfer_fifo_price_money",
				name: t("common.word.transfer_fifo_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "end_amount",
				name: t("common.word.end_amount"),
			},
			{
				id: "stocktaking_fifo_price_money",
				name: t("common.word.stocktaking_fifo_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "stocktaking_amount",
				name: t("common.word.stocktaking_amount"),
			},
			{
				id: "end_fifo_price_money",
				name: t("common.word.end_fifo_price_money"),
				render: (item: MoneyApi) => FormatMoney(item),
			},
		],
		filters: [
			...getCommonFilters(t),
			{
				id: "start_amount",
				name: t("common.word.start_amount"),
				type: "number",
			},
			{
				id: "start_fifo_price_money",
				name: t("common.word.start_fifo_price_money"),
				type: "number",
			},
			{
				id: "purchase_amount",
				name: t("common.word.purchase_amount"),
				type: "number",
			},
			{
				id: "purchase_price_money",
				name: t("common.word.purchase_price_money"),
				type: "number",
			},
			{
				id: "sold_amount",
				name: t("modules.product_report.field.sold_amount.title"),
				type: "number",
			},
			{
				id: "sold_fifo_price_money",
				name: t("common.word.sold_fifo_price_money"),
				type: "number",
			},
			{
				id: "transfer_amount",
				name: t("common.word.transfer_amount"),
				type: "number",
			},
			{
				id: "transfer_fifo_price_money",
				name: t("common.word.transfer_fifo_price_money"),
				type: "number",
			},
			{
				id: "end_amount",
				name: t("common.word.end_amount"),
				type: "number",
			},
			{
				id: "end_fifo_price_money",
				name: t("common.word.end_fifo_price_money"),
				type: "number",
			},
			{
				id: "bundle_amount",
				name: t("modules.product.field.bundle_amount.title"),
				type: "number",
			},
			{
				id: "bundle_fifo_price_money",
				name: t("modules.product.field.bundle_fifo_price_money.title"),
				type: "number",
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: INITIAL_SELECTED_COLUMNS,
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[1],
						condition: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[0],
					},
				],
				groups: ["PRODUCT"],
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			delete params.chart_type;
			return Promise.all([
				apiOrganization.getOrganizationProductReports(reportParamsConverter(params), {
					cancelToken: sourceToken?.token,
				}),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.product_report.header.title"),
			filename: t("modules.product_report.header.title"),
			organization: organization.name,
		},
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["PRODUCT", "PRODUCT_CATEGORY"], t);
	data.filter_values = convertFilterValuesToSnakeCase(data.filter_values || []);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

export const OrganizationReportProductIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	const customFieldResourceTypes = [
		{
			type: "PRODUCT",
			name: t(`enums.custom_fields.resources.PRODUCT`),
		},
		{
			type: "PRODUCT_CATEGORY",
			name: t(`enums.custom_fields.resources.PRODUCT_CATEGORY`),
		},
	];

	useEffect(() => {
		handleChangeTabTitle(t("modules.product_report.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<>
			{!isMobile && <Header title={t("modules.product_report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
			</Suspense>
		</>
	);
};
